import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  Divider,
} from "@mui/material";
import MUIDataTable from "mui-datatables";
import { Link, useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import EditIcon from "@mui/icons-material/Edit";
import { IconButton, Tooltip } from "@mui/material";
import { getVoucherDetail } from "../../apis/cart/apis";

export default function VoucherDetailScreen() {
  const { id } = useParams();
	const navigate = useNavigate();
  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchVoucher = async () => {
      try {
        setLoading(true);

        const response = await getVoucherDetail(id);

        setVoucher(response.data);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    fetchVoucher();
  }, [id]);

  const leftAlignedColumn = {
    filter: false,
    sort: false,

    setCellHeaderProps: () => ({
      style: {
        textAlign: "left",
        fontWeight: 600,
      },
    }),

    setCellProps: () => ({
      style: {
        textAlign: "left",
      },
    }),
  };

  const offerColumns = [
    {
      name: "name",
      label: "Offer",
      options: {
        ...leftAlignedColumn,
        customBodyRender: (value, tableMeta) => {
          const row = voucher.offers[tableMeta.rowIndex];

          return (
            <Link
              to={`/w86zjruk6q/offers/${row.id}`}
              style={{
                textDecoration: "none",
              }}
            >
              {value}
            </Link>
          );
        },
      },
    },
    {
      name: "start_datetime",
      label: "Start Date",
      options: {
        ...leftAlignedColumn,
        customBodyRender: (value) =>
          value ? moment(value).format("MMM D, YYYY, h:mm A") : "-",
      },
    },
    {
      name: "end_datetime",
      label: "End Date",
      options: {
        ...leftAlignedColumn,
        customBodyRender: (value) =>
          value ? moment(value).format("MMM D, YYYY, h:mm A") : "-",
      },
    },
    {
      name: "is_available",
      label: "Status",
      options: {
        ...leftAlignedColumn,
        customBodyRender: (value) => (
          <Chip
            label={value ? "Active" : "Inactive"}
            color={value ? "success" : "error"}
            size="small"
          />
        ),
      },
    },
    {
      name: "priority",
      label: "Priority",
      options: leftAlignedColumn,
    },
    {
      name: "incentive",
      label: "Incentive",
      options: leftAlignedColumn,
    },
    {
      name: "condition",
      label: "Condition",
      options: leftAlignedColumn,
    },
  ];

  const orderColumns = [
    {
      name: "order_number",
      label: "Order Number",
      options: {
        ...leftAlignedColumn,
        customBodyRender: (value, tableMeta) => {
          const row = voucher.order_discounts[tableMeta.rowIndex];

          return (
            <Link
              to={`/w86zjruk6q/cart/orders/${row.order_id}/detail`}
              style={{ textDecoration: "none" }}
            >
              {value}
            </Link>
          );
        },
      },
    },
    {
      name: "order_total",
      label: "Order Total",
      options: {
        ...leftAlignedColumn,
        customBodyRender: (value) => `₹${value}`,
      },
    },
    {
      name: "discount",
      label: "Discount",
      options: {
        ...leftAlignedColumn,
        customBodyRender: (value) => `₹${value}`,
      },
    },
    {
      name: "order_date",
      label: "Date Placed",
      options: {
        ...leftAlignedColumn,
        customBodyRender: (value) =>
          moment(value).format("MMM D, YYYY, h:mm A"),
      },
    },
  ];

  const tableOptions = {
    selectableRows: "none",
    download: false,
    print: false,
    filter: false,
    search: false,
    viewColumns: false,
    responsive: "standard",
    elevation: 0,
  };

  if (loading) {
    return (
      <Box p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (!voucher) {return null;}

  return (
    <Box p={3}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4">{voucher.name}</Typography>

        <Tooltip title="Edit Voucher">
          <IconButton
            color="primary"
            onClick={() => navigate(`/w86zjruk6q/vouchers/${voucher.id}/edit`)}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2">Voucher Status</Typography>

            <Chip
              label={voucher.status}
              color={voucher.status === "Active" ? "success" : "error"}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2">Total Discount</Typography>

            <Typography variant="h6">
              ₹{Number(voucher.total_discount).toFixed(2)}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2">Number of Orders</Typography>

            <Typography variant="h6">{voucher.num_orders}</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2">Cart Additions</Typography>

            <Typography variant="h6">{voucher.num_carts}</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={4}>
        <Grid item xs={12} md={5}>
          <Paper elevation={2} sx={{ p: 2, height: "100%" }}>
            <Typography variant="h5" mb={5}>
              Voucher Details
            </Typography>

            <Divider />

            <Box sx={{ mt: 2 }}>
              {[
                {
                  label: "Name",
                  value: voucher.name,
                },
                {
                  label: "Code",
                  value: voucher.code,
                },
                {
                  label: "Usage",
                  value: voucher.usage,
                },
                {
                  label: "Start Date",
                  value: moment(voucher.start_datetime).format(
                    "MMMM D, YYYY, h:mm a",
                  ),
                },
                {
                  label: "End Date",
                  value: voucher.end_datetime
                    ? moment(voucher.end_datetime).format(
                        "MMMM D, YYYY, h:mm a",
                      )
                    : "-",
                },
                {
                  label: "Number of Offers",
                  value: voucher.num_offers,
                },
              ].map((field) => (
                <Box key={field.label} display="flex" mb={1.5}>
                  <Typography
                    sx={{
                      minWidth: 150,
                      fontWeight: 500,
                    }}
                  >
                    {field.label}:
                  </Typography>

                  <Typography>{field.value}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <MUIDataTable
            title="Attached Offers"
            data={voucher.offers}
            columns={offerColumns}
            options={tableOptions}
          />
        </Grid>
      </Grid>

      <Box mt={4}>
        <MUIDataTable
          title="Recent Orders"
          data={voucher.order_discounts}
          columns={orderColumns}
          options={tableOptions}
        />
      </Box>
    </Box>
  );
}
